import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { CheckIcon, ArrowTopRightOnSquareIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import type { DataTableDef, TablemaintField } from '../../config/dataTables.registry';
import { MultiSelectField, SelectField, TextField, TextAreaField, Toggle, deletedNoun, isDeletedFlag, normalizeOptions } from './fields';
import DateField from './DateField';
import EditHistoryLink from './EditHistoryLink';
import { DRAWER_ACTIONS } from './drawerActions';
import { isValidEmailList } from '../../utils/email';
import { titleCase } from '../../utils/titleCase';
import { useFieldOptions, type FieldOptionsMap } from './useFieldOptions';
import { LOOKUP_FIELDS } from './lookupFields';

// Lazy-loaded so the Google-Maps dependency stays out of the shared tablemaint bundle
// (and test import graph) until a user opens the ZIP map from a `mapZip` field.
const ZipLocationMapModal = lazy(() => import('../ZipLocationMapModal'));

// isDeletedFlag / deletedNoun live in ./fields so the bespoke drawers share them.
const isTruthy = isDeletedFlag;

/** Shared drawer width. The old inline default was maxWidth="lg" (28rem), which
 *  left ~13rem per column in the 2-col field grid — only one of the 39 bespoke
 *  drawers was that narrow; they clustered at 40–60rem. A table can widen further
 *  with cfg.drawerWidth. Drawer forces full width on mobile either way. */
const DEFAULT_DRAWER_WIDTH = '44rem';

/** Legacy FK recno columns store 0 for "unset": show 0 as blank in the form... */
const fkSeed = (v: any): string => (v && String(v) !== '0' ? String(v) : '');
/** ...and write a cleared value back as '0', not ''. */
const fkOut = (v: any): string => (String(v ?? '').trim() || '0');

/** Toast text handed back to the host after a successful save. */
export interface SaveNotification {
  title: string;
  message: string;
}

export interface TablemaintDrawerProps {
  /** Field specs + table flags. The same registry entry <TablemaintPage> renders. */
  cfg: DataTableDef;
  isOpen: boolean;
  /** The row being edited. null + no `recno` = adding a new record. */
  row: TablemaintRow | null;
  /** Edit a record the caller only has an id for — the drawer fetches it itself.
   *  For surfaces that link to a record without holding its row (an EDI board, a
   *  pro-detail modal). Ignored when `row` is given. */
  recno?: number | string | null;
  /** Drawer header override. Defaults to "Edit/Add {singular}"; standalone callers
   *  usually want the record in the title. Pass a function to build it from the row
   *  (null until a fetch-by-id record arrives). */
  title?: string | ((row: TablemaintRow | null) => string);
  /** Drawer width (e.g. '42rem'). Overrides cfg.drawerWidth; both default to 44rem. */
  width?: string;
  /** Field values pinned by the surface that opened the drawer — pre-filled on create,
   *  and rendered read-only so they can't be edited away from the parent context. Used
   *  when a child record is opened FROM its parent (SCAC Info opening a SCAC Location
   *  pins the SCAC and Region). Applied on top of the row on save, so a create carries
   *  them even though their inputs are disabled. */
  presetValues?: Record<string, string>;
  /** Extra content rendered below the generated fields — a child subtable
   *  (contacts, documents, line items) that the generic field specs can't express.
   *  Only rendered when editing an existing row, since a subtable needs its parent
   *  recno. Lets a table keep the generic drawer instead of replacing all of it. */
  renderPanels?: (row: TablemaintRow) => React.ReactNode;
  onClose: () => void;
  /** Called after a successful create/update, before onClose. The host shows the
   *  notification (if any) and reloads its data; awaited so the drawer stays in
   *  its saving state until the host's refetch settles, matching the original
   *  in-page behavior. */
  onSaved: (notification?: SaveNotification) => void | Promise<void>;
  /** Pre-fetched `optionsEndpoint` results. Pass when the host already loaded them
   *  (<TablemaintPage> needs the same map for multiselect grid cells) to avoid a
   *  duplicate fetch; omit when mounting the drawer standalone and it fetches its own. */
  options?: FieldOptionsMap;
}

/**
 * The generic, field-spec-driven table-maintenance drawer.
 *
 * Extracted from <TablemaintPage> so it can be mounted on its own — from a board,
 * a modal, or any surface that wants to edit one engine row — rather than only
 * inline inside the Data Management page. <TablemaintPage> renders this whenever
 * a table has no bespoke drawer registered in CUSTOM_DRAWERS.
 *
 * Owns the whole edit experience: form state seeded from `row`, required
 * asterisks, red-outline invalid fields, the error banner with auto-scroll, the
 * soft-delete toggle, and the create/update save (changed fields only).
 */
const TablemaintDrawer: React.FC<TablemaintDrawerProps> = ({
  cfg, isOpen, row: providedRow, recno, title, width, renderPanels, presetValues,
  onClose, onSaved, options: providedOptions,
}) => {
  const idKey = cfg.idKey ?? 'recno';
  const softDeleteCol = cfg.softDelete ?? 'deleted';
  const softDeleteInvert = !!cfg.softDeleteInvert; // column is an ACTIVE flag (Y=visible)
  const hasSoftDelete = !cfg.noSoftDelete;
  const singular = cfg.singular ?? cfg.label;
  const fields = cfg.fields ?? [];

  const editableFields = useMemo<TablemaintField[]>(
    () => fields.filter((f) => f.editable !== false),
    [fields],
  );
  // Non-editable fields shown as a read-only "Details" block (opt-in via
  // cfg.showReadOnlyContext) — gives the editable inputs surrounding context. Includes
  // `inTable: false` fields (drawer-only read-only context, e.g. EDI Invoices' Sent By /
  // File Name that aren't grid columns); `inTable` governs the grid, not the drawer.
  const readOnlyContextFields = useMemo<TablemaintField[]>(
    () => (cfg.showReadOnlyContext ? fields.filter((f) => f.editable === false && !f.group) : []),
    [cfg.showReadOnlyContext, fields],
  );
  const inputFields = useMemo(
    () => editableFields.filter((f) => !f.group && f.type !== 'yn' && f.type !== 'toggle'),
    [editableFields],
  );
  const toggleFields = useMemo(
    () => editableFields.filter((f) => !f.group && (f.type === 'yn' || f.type === 'toggle')),
    [editableFields],
  );

  // Grouped fields become one full-width <fieldset> per group, legend = group name,
  // in first-seen registry order. A group carries whatever its fields are: read-only
  // ones render read-only, the rest as inputs / toggles. This is how the bespoke
  // drawers organised themselves ('Identity (read-only)', 'Settings', 'Notes'), and
  // giving each section the drawer's full width keeps dense groups readable.
  const groupedSections = useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, TablemaintField[]>();
    fields.forEach((f) => {
      if (!f.group) return;
      if (!byGroup.has(f.group)) { byGroup.set(f.group, []); order.push(f.group); }
      byGroup.get(f.group)!.push(f);
    });
    return order.map((name) => {
      const gf = byGroup.get(name)!;
      return {
        name,
        readOnly: gf.filter((f) => f.editable === false),
        inputs: gf.filter((f) => f.editable !== false && f.type !== 'yn' && f.type !== 'toggle'),
        toggles: gf.filter((f) => f.editable !== false && (f.type === 'yn' || f.type === 'toggle')),
      };
    });
  }, [fields]);

  const isDeleted = useCallback(
    // Inverted columns store an ACTIVE flag: 'Y' = visible, anything else = deleted.
    (r: TablemaintRow) => (softDeleteInvert ? !isTruthy(r[softDeleteCol]) : isTruthy(r[softDeleteCol])),
    [softDeleteCol, softDeleteInvert],
  );

  // Only fetch our own option lists when the host didn't already supply them
  // (passing [] short-circuits the hook without breaking the rules of hooks).
  const ownOptions = useFieldOptions(providedOptions ? [] : fields);
  const dynamicOptions = providedOptions ?? ownOptions;

  // Fetch-by-id mode: the caller gave us a recno, not a row. `isAdding` stays
  // false here — a null row with a recno means "loading", not "creating".
  const fetchById = !providedRow && recno != null && recno !== '';
  const [fetched, setFetched] = useState<TablemaintRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen || !fetchById) return;
    let alive = true;
    setLoading(true);
    setLoadError(null);
    tablemaintService.get(cfg.key, recno as number | string)
      .then((r) => { if (alive) setFetched(r); })
      .catch((e: any) => {
        if (alive) setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load record.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isOpen, fetchById, cfg.key, recno]);

  // Grid rows carry only the table's `list_columns`, but a drawer may render fields
  // the list doesn't return (vendors' Credit Terms / Created / MAS Tag). When any
  // rendered field is absent from the row, fetch the full record and re-seed from it
  // — what every bespoke drawer did with its own tablemaintService.get. Keys that ARE
  // present seed immediately, so the drawer never flashes empty.
  // Check the companion columns too, not just f.key. A refNameKey / deletedKey outside
  // list_columns (upload-portmap's `pport`) leaves the picker showing a bare recno with
  // no name and no deleted note — and nothing else would trigger the fetch.
  const needsHydration = !!providedRow && fields.some((f) =>
    [f.key, f.refNameKey, f.deletedKey].some((k) => k && providedRow[k] === undefined));
  const [hydrated, setHydrated] = useState<TablemaintRow | null>(null);
  useEffect(() => {
    if (!isOpen || !needsHydration || !providedRow) return;
    let alive = true;
    tablemaintService.get(cfg.key, providedRow[cfg.idKey ?? 'recno'])
      .then((r) => { if (alive) setHydrated(r); })
      .catch(() => { /* keep the partial grid row — fields stay blank, as before */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, needsHydration, cfg.key, providedRow?.[cfg.idKey ?? 'recno']]);

  const row = hydrated ?? providedRow ?? (fetchById ? fetched : null);
  const isAdding = !providedRow && !fetchById;

  const [form, setForm] = useState<TablemaintRow>({});
  const [formDeleted, setFormDeleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // ZIP shown in the map modal, opened from a `mapZip` field's "show on map" button.
  const [zipMapValue, setZipMapValue] = useState<string | null>(null);

  // Display text for each `type: 'lookup'` field. The form holds the FK recno, but an
  // async picker also needs the NAME to show — on open it comes from the row's
  // refNameKey column, and thereafter from whatever the user picks or types.
  const [lookupNames, setLookupNames] = useState<Record<string, string>>({});

  // Build a fresh form from the field specs (defaults per type).
  const emptyForm = useCallback((): TablemaintRow => {
    const f: TablemaintRow = {};
    editableFields.forEach((fld) => {
      if (fld.defaultValue !== undefined) { f[fld.key] = fld.defaultValue; return; }
      f[fld.key] = fld.type === 'multiselect' ? [] : fld.type === 'toggle' ? false : fld.type === 'yn' ? 'N' : '';
    });
    return f;
  }, [editableFields]);

  const formFromRow = useCallback(
    (r: TablemaintRow): TablemaintRow => {
      const f: TablemaintRow = {};
      editableFields.forEach((fld) => {
        if (fld.type === 'multiselect') f[fld.key] = Array.isArray(r[fld.key]) ? [...(r[fld.key] as string[])] : [];
        else if (fld.type === 'toggle') f[fld.key] = Boolean(r[fld.key]);
        else if (fld.type === 'yn') f[fld.key] = r[fld.key] === 'Y' ? 'Y' : 'N';
        else if (fld.fkZeroBlank) f[fld.key] = fkSeed(r[fld.key]);
        else f[fld.key] = r[fld.key] ?? '';
      });
      return f;
    },
    [editableFields],
  );

  // Seed the form when the drawer opens (and re-seed if it's pointed at a different
  // record while open). Done during render rather than in an effect so the inputs
  // are already populated on the first painted frame — an effect would flash empty.
  // In fetch-by-id mode there is nothing to seed from until the row arrives.
  const openKey = !isOpen ? '' : row
    ? `edit:${String(row[idKey])}${hydrated ? ':full' : ''}`
    : isAdding ? 'add' : '';
  const [seededFor, setSeededFor] = useState('');
  if (isOpen && openKey && seededFor !== openKey) {
    setSeededFor(openKey);
    setForm({ ...(row ? formFromRow(row) : emptyForm()), ...(presetValues ?? {}) });
    setFormDeleted(row ? isDeleted(row) : false);
    setShowErrors(false);
    setSaveError(null);
    setLookupNames(Object.fromEntries(
      fields.filter((f) => f.type === 'lookup' && f.refNameKey)
        .map((f) => [f.key, String((row ?? {})[f.refNameKey!] ?? '')]),
    ));
  }
  if (!isOpen && seededFor !== '') { setSeededFor(''); if (hydrated) setHydrated(null); }

  // A field is required when statically flagged, OR when its `requiredWhen` condition
  // currently holds (e.g. Driver Access Name required while Driver Access = 'Y'). Drives
  // the asterisk, red outline, and the error banner — the backend enforces the same rule.
  const fieldRequired = (f: TablemaintField, values: TablemaintRow): boolean =>
    !!f.required ||
    (!!f.requiredWhen && String(values[f.requiredWhen.field] ?? '') === f.requiredWhen.equals);
  const isFieldRequired = (f: TablemaintField): boolean => fieldRequired(f, form);

  // Field validation, derived from the field specs: required + optional format checks
  // (integer / email) that only fire on a non-empty value.
  const validation = useMemo(() => {
    const errors: string[] = [];
    const invalid = new Set<string>();
    editableFields.forEach((f) => {
      const raw = String(form[f.key] ?? '').trim();
      const empty = f.type === 'multiselect'
        ? !(Array.isArray(form[f.key]) && (form[f.key] as string[]).length > 0)
        : !raw;
      if (fieldRequired(f, form) && empty) {
        errors.push(`${f.label} is required.`);
        invalid.add(f.key);
      } else if (!empty) {
        if (f.integer && !(/^\d+$/.test(raw) && Number(raw) > 0)) {
          errors.push(`${f.label} must be a positive whole number.`);
          invalid.add(f.key);
        }
        if (f.decimal && !(/^\d+(\.\d+)?$/.test(raw) && Number(raw) > 0)) {
          errors.push(`${f.label} must be a positive number.`);
          invalid.add(f.key);
        }
        if (f.email && !isValidEmailList(raw, { semicolon: true })) {
          errors.push(`${f.label} must be valid email address(es), separated by commas.`);
          invalid.add(f.key);
        }
      }
    });
    // Cross-field "at least one of" (cfg.requireOneOf). '0' counts as blank, matching
    // the backend validators' treatment of an unset FK.
    const req = cfg.requireOneOf;
    if (req && req.fields.every((k) => {
      const v = form[k];
      return Array.isArray(v) ? v.length === 0 : ['', '0'].includes(String(v ?? '').trim());
    })) {
      errors.push(req.message);
    }
    return { errors, invalid };
  }, [editableFields, form, cfg.requireOneOf]);

  const bannerErrors = [...(showErrors ? validation.errors : []), ...(saveError ? [saveError] : [])];
  const fieldInvalid = (key: string) => showErrors && validation.invalid.has(key);
  const setField = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  // Scroll the drawer to the top so the banner is seen when a blocked save raises errors.
  useEffect(() => {
    if (showErrors || saveError) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showErrors, saveError]);

  const sameField = (f: TablemaintField, formVal: any, r: TablemaintRow): boolean => {
    if (f.type === 'toggle') return Boolean(formVal) === Boolean(r[f.key]);
    if (f.type === 'yn') return (formVal === 'Y' ? 'Y' : 'N') === (r[f.key] === 'Y' ? 'Y' : 'N');
    if (f.type === 'multiselect') {
      const a = Array.isArray(formVal) ? [...formVal] : [];
      const b = Array.isArray(r[f.key]) ? [...(r[f.key] as string[])] : [];
      return a.length === b.length && a.sort().join('\u0000') === b.sort().join('\u0000');
    }
    if (f.fkZeroBlank) return fkOut(formVal) === fkOut(r[f.key]);
    return String(formVal ?? '') === String(r[f.key] ?? '');
  };

  const handleSave = useCallback(async () => {
    if (validation.errors.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      let notification: SaveNotification;
      // Keyed on isAdding, not `!row` — in fetch-by-id mode a null row means the
      // record hasn't loaded (or failed to), never "create a new one".
      if (!isAdding && row) {
        // Send only changed fields, plus the soft-delete flag if it changed.
        const patch: TablemaintRow = {};
        editableFields.forEach((f) => {
          if (!sameField(f, form[f.key], row)) patch[f.key] = f.fkZeroBlank ? fkOut(form[f.key]) : form[f.key];
        });
        if (hasSoftDelete && formDeleted !== isDeleted(row)) patch[softDeleteCol] = formDeleted ? 'Y' : 'N';
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        await tablemaintService.update(cfg.key, row[idKey], patch);
        notification = { title: 'Saved', message: `${singular} updated.` };
      } else if (isAdding) {
        // Create — soft-delete isn't offered on Add; backend defaults it.
        const payload: TablemaintRow = {};
        editableFields.forEach((f) => {
          payload[f.key] = f.fkZeroBlank ? fkOut(form[f.key]) : form[f.key];
        });
        Object.assign(payload, presetValues ?? {});
        await tablemaintService.create(cfg.key, payload);
        notification = { title: 'Created', message: `${singular} added.` };
      } else {
        return; // record still loading / failed to load — Save is disabled anyway
      }
      // Notify → close → await the host's refetch, preserving the original
      // in-page ordering (the toast + close land in one React batch).
      const reloaded = onSaved(notification);
      onClose();
      await reloaded;
    } catch (err: any) {
      console.error(`Error saving ${cfg.key}:`, err);
      setSaveError(err?.response?.data?.detail || err?.message || 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [validation, row, isAdding, editableFields, form, formDeleted, isDeleted, hasSoftDelete,
      softDeleteCol, cfg.key, idKey, singular, presetValues, onClose, onSaved]);

  const renderInput = (f: TablemaintField) => {
    const disabled = isSaving || (f.createOnly && !!row) || f.key in (presetValues ?? {});
    if (f.type === 'date') {
      return (
        <DateField
          key={f.key}
          label={f.label}
          required={isFieldRequired(f)}
          disabled={disabled}
          value={String(form[f.key] ?? '')}
          onChange={(v) => setField(f.key, v)}
          invalid={fieldInvalid(f.key)}
        />
      );
    }
    if (f.type === 'textarea') {
      return (
        <TextAreaField
          key={f.key}
          label={f.label}
          required={isFieldRequired(f)}
          disabled={disabled}
          value={String(form[f.key] ?? '')}
          onChange={(v) => setField(f.key, v)}
          invalid={fieldInvalid(f.key)}
        />
      );
    }
    if (f.type === 'multiselect') {
      const opts = f.optionsEndpoint
        ? normalizeOptions(dynamicOptions[f.optionsEndpoint] ?? [])
        : normalizeOptions(f.options);
      return (
        <MultiSelectField
          key={f.key}
          label={f.label}
          required={isFieldRequired(f)}
          disabled={disabled}
          loading={!!f.optionsEndpoint && !dynamicOptions[f.optionsEndpoint]}
          value={Array.isArray(form[f.key]) ? (form[f.key] as string[]) : []}
          onChange={(v) => setField(f.key, v)}
          options={opts}
          placeholder={f.placeholder}
        />
      );
    }
    if (f.type === 'lookup') {
      const Picker = f.lookupTarget ? LOOKUP_FIELDS[f.lookupTarget] : undefined;
      if (!Picker) return null; // unknown target — better blank than a crash
      // Same deleted-reference story as the select branch below, but a lookup can't
      // show it the same way: these targets are searched per keystroke (ACTIVE rows
      // only) rather than picked from a list, so the stored FK resolves to a name and
      // looks perfectly healthy. Give it the SAME orange invalid highlight the selects
      // get for an unknown value, plus a note saying which of the two it is.
      //
      // Both are scoped to the ORIGINAL recno: once the user picks a live record the
      // warning would describe a reference that is no longer there.
      const stillOriginal = (Number(form[f.key]) || 0) === (Number(row?.[f.key]) || 0);
      const refDeleted = !!f.deletedKey && !!row && isTruthy(row[f.deletedKey]) && stillOriginal;
      // "Not found": a non-zero FK whose name column came back empty — the target row
      // is gone entirely, so no deleted flag is emitted for it either. Requires the
      // name column to be PRESENT (undefined = still hydrating, don't flash orange).
      const refMissing = !!f.refNameKey && !!row && row[f.refNameKey] !== undefined
        && stillOriginal && (Number(form[f.key]) || 0) > 0
        && !String(row[f.refNameKey] ?? '').trim() && !refDeleted;
      return (
        <div key={f.key}>
          <Picker
            label={f.label}
            required={isFieldRequired(f)}
            disabled={disabled}
            invalid={refDeleted || refMissing}
            value={{ recno: Number(form[f.key]) || 0, name: lookupNames[f.key] ?? '' }}
            onChange={(v) => {
              // Free-typed text unlinks: recno 0, text kept. fkZeroBlank writes '' as '0'.
              setField(f.key, v.recno ? String(v.recno) : '');
              setLookupNames((prev) => ({ ...prev, [f.key]: v.name }));
            }}
          />
          {(refDeleted || refMissing) && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {refDeleted
                ? `This ${deletedNoun(f.label)} has been deleted.`
                : `This ${deletedNoun(f.label)} could not be found.`}
            </p>
          )}
        </div>
      );
    }
    if (f.type === 'select' || f.optionsEndpoint) {
      const opts = f.optionsFromRows
        ? normalizeOptions(dynamicOptions[f.key] ?? [])
        : f.optionsEndpoint
        ? normalizeOptions(dynamicOptions[f.optionsEndpoint] ?? [])
        : normalizeOptions(f.options);
      // A since-deleted target is absent from the (active-only) option list. Keep it
      // readable as "Name  ·  #recno" rather than a bare number, and say why.
      const refName = f.refNameKey && row ? String(row[f.refNameKey] ?? '').trim() : '';
      const refDeleted = !!f.deletedKey && !!row && isTruthy(row[f.deletedKey]);
      return (
        <SelectField
          key={f.key}
          label={f.label}
          required={isFieldRequired(f)}
          disabled={disabled}
          clearable={f.clearable}
          showDescriptions={f.showDescriptions}
          showSelectedInfo={f.showSelectedInfo}
          value={String(form[f.key] ?? '')}
          onChange={(v) => setField(f.key, v)}
          options={opts}
          allowNewOptions={f.allowNewOptions}
          invalidLabel={refName ? `${titleCase(refName)}  ·  #${String(form[f.key] ?? '')}` : undefined}
          invalidNote={refDeleted ? `This ${deletedNoun(f.label)} has been deleted.` : undefined}
          invalid={fieldInvalid(f.key)}
        />
      );
    }
    const textField = (
      <TextField
        key={f.key}
        label={f.label}
        required={isFieldRequired(f)}
        disabled={disabled}
        type={f.type === 'number' ? 'number' : 'text'}
        maxLength={f.maxLength}
        placeholder={f.placeholder}
        value={String(form[f.key] ?? '')}
        onChange={(v) => setField(f.key, v)}
        invalid={fieldInvalid(f.key)}
      />
    );
    if (!f.mapZip) return textField;
    // "Show on map" button beneath a ZIP field — enabled only when a ZIP is present.
    const zipVal = String(form[f.key] ?? '').trim();
    return (
      <div key={f.key}>
        {textField}
        <div className="mt-1.5 text-xs">
          <button
            type="button"
            onClick={() => setZipMapValue(zipVal)}
            disabled={!zipVal}
            className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MapPinIcon className="w-3.5 h-3.5" /> Show on map
          </button>
        </div>
      </div>
    );
  };

  const renderReadOnly = (f: TablemaintField, r: TablemaintRow) => (
    <div key={f.key} className="flex flex-col">
      <span className="text-xs font-medium text-muted">{f.label}</span>
      <span className="text-sm text-gray-800 dark:text-gray-200 break-words">
        {String(r[f.key] ?? '') || '—'}
      </span>
    </div>
  );

  const renderToggle = (f: TablemaintField) => {
    const disabled = isSaving || (f.createOnly && !!row) || f.key in (presetValues ?? {});
    const checked = f.type === 'yn' ? form[f.key] === 'Y' : Boolean(form[f.key]);
    return (
      <Toggle
        key={f.key}
        label={f.label}
        checked={checked}
        disabled={disabled}
        onChange={(v) => setField(f.key, f.type === 'yn' ? (v ? 'Y' : 'N') : v)}
      />
    );
  };

  if (cfg.readonly) return null;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={(typeof title === 'function' ? title(row) : title)
          ?? (isAdding ? `Add ${singular}` : `Edit ${singular}`)}
        position="right"
        width={width ?? cfg.drawerWidth ?? DEFAULT_DRAWER_WIDTH}
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || loading || !!loadError}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading…</div>
        ) : loadError ? (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {loadError}
          </div>
        ) : (
        <div className="space-y-6">
          <div ref={topRef} />
          {row && (
            <div className="flex justify-end items-center gap-4">
              {cfg.drawerActions?.map((key) => (
                <React.Fragment key={key}>{DRAWER_ACTIONS[key]?.(row)}</React.Fragment>
              ))}
              {cfg.extraHistoryLink && (
                <a
                  href={cfg.extraHistoryLink.href(row)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" /> {cfg.extraHistoryLink.label}
                </a>
              )}
              <EditHistoryLink table={cfg.key} recno={Number(row[idKey])} />
            </div>
          )}
          {bannerErrors.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                Please fix the following before saving:
              </p>
              <ul className="mt-1 list-disc list-inside text-sm text-red-700 dark:text-red-300">
                {bannerErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {row && readOnlyContextFields.length > 0 && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted mb-3">Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                {readOnlyContextFields.map((f) => renderReadOnly(f, row))}
              </div>
            </div>
          )}

          {inputFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inputFields.map((f) => (
                <div key={f.key} className={f.fullWidth ? 'md:col-span-2' : undefined}>
                  {renderInput(f)}
                </div>
              ))}
            </div>
          )}

          {toggleFields.length > 0 && (
            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <legend className="px-2 text-sm font-semibold text-label">Options</legend>
              {toggleFields.map(renderToggle)}
            </fieldset>
          )}

          {/* Field groups — each its own full-width <fieldset>, so a dense section
              gets the whole drawer rather than half of a 2-col grid. */}
          {groupedSections.map((g) => {
            // A read-only group needs a row to read from; an all-read-only group is
            // therefore skipped while adding.
            if (!row && g.inputs.length === 0 && g.toggles.length === 0) return null;
            return (
              <fieldset key={g.name} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <legend className="px-2 text-sm font-semibold text-label">
                  {g.name}
                  {cfg.requireOneOf?.group === g.name && cfg.requireOneOf.hint && (
                    <span className="ml-1 text-xs font-normal text-gray-400">({cfg.requireOneOf.hint})</span>
                  )}
                </legend>
                {row && g.readOnly.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                    {g.readOnly.map((f) => renderReadOnly(f, row))}
                  </div>
                )}
                {g.inputs.length > 0 && (
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4${row && g.readOnly.length > 0 ? ' mt-4' : ''}`}>
                    {g.inputs.map((f) => (
                      <div key={f.key} className={f.fullWidth ? 'md:col-span-2' : undefined}>
                        {renderInput(f)}
                      </div>
                    ))}
                  </div>
                )}
                {g.toggles.length > 0 && (
                  <div className={g.readOnly.length > 0 || g.inputs.length > 0 ? 'mt-4' : undefined}>
                    {g.toggles.map(renderToggle)}
                  </div>
                )}
              </fieldset>
            );
          })}

          {/* Child subtables (contacts, documents, line items) — a table can keep
              the generic drawer and add just the part the field specs can't express. */}
          {row && renderPanels?.(row)}

          {/* Soft-delete — only when editing an existing row (matches Dispatchers).
              Inverted tables (active flag) show an "Active" toggle + red warning. */}
          {row && hasSoftDelete && (
            softDeleteInvert ? (
              <div className={`rounded-lg border p-3 ${formDeleted ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
                <Toggle label="Active" checked={!formDeleted} onChange={(v) => setFormDeleted(!v)} disabled={isSaving} />
                {formDeleted && (
                  <p className="mt-1 ml-7 text-xs font-medium text-red-700 dark:text-red-300">
                    Inactive records are hidden from lookups. Re-check Active and save to restore.
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <Toggle label="Deleted" checked={formDeleted} onChange={setFormDeleted} disabled={isSaving} />
                <p className="mt-1 ml-7 text-xs text-muted">
                  Soft-deletes this record — hidden from filters but preserved in history. Uncheck and save to
                  restore.
                </p>
              </div>
            )
          )}

        </div>
        )}
      </Drawer>

      {/* ZIP map opened from a mapZip field's "show on map" button (lazy-loaded). */}
      {zipMapValue !== null && (
        <Suspense fallback={null}>
          <ZipLocationMapModal zip={zipMapValue} onClose={() => setZipMapValue(null)} />
        </Suspense>
      )}
    </>
  );
};

export default TablemaintDrawer;
